/**
 * Is the BUILT mod-core new enough for this shell? (RFC 0284 S8a3.)
 *
 * WHY THIS EXISTS, precisely. The app runs from a Vite dev server and imports
 * mod-core as a BUILT package. Editing a shell file hot-reloads it immediately;
 * editing mod-core does nothing until somebody runs its build. So any commit
 * that adds a mod-core export and calls it from the shell leaves a window in
 * which the shell asks for something that is not there.
 *
 * What that looked like was a dead window. A missing named export is not an
 * error under Vite's interop — it is `undefined` — so the first call throws
 * somewhere deep in a render, React unmounts the tree, and the app is blank
 * with a stack that names the wrong thing. Nothing in it says "rebuild
 * mod-core", which is the only useful sentence.
 *
 * A LIST OF NAMES, checked once at mount. Not a version number, because a
 * version tells you what somebody remembered to bump and this tells you what is
 * actually missing. Not a check per caller, because that is a check somebody
 * forgets to add on the commit that needs it most.
 *
 * ADD TO THIS LIST when the shell starts calling a new mod-core export. The
 * cost of forgetting is the blank window this exists to prevent; the cost of an
 * entry that is no longer used is one string.
 */

import * as core from '@mod/core/model'

/**
 * Everything the shell calls that arrived recently enough to be missing from a
 * build somebody has lying around. Older exports are not listed: if
 * `createSession` is gone, the package is not stale, it is broken.
 */
const REQUIRED = [
  // RFC 0284 §4e onwards: layouts, their slots, geometry and panes.
  'installLayout',
  'setLayoutSlots',
  'setLayoutGeometry',
  'setLayoutTree',
  'layoutsIn',
  'layoutTree',
  'resolveLayout',
  'slotFill',
  'slotGeometry',
  // S5, S6b: installing chrome and a thread renderer.
  'installChrome',
  'installRenderer',
  // S6g: per-app settings.
  'appSettings',
  'setAppSetting',
  // S6m: open with.
  'resolveHandler',
  'handledKeys',
  'setHandler',
  // S8a: status on the registry.
  'projectSessionStatuses',
  'registryStatus',
] as const

/**
 * The names this shell needs and the built package does not have, or `[]`.
 *
 * Cheap enough to call on every render, and it never changes within a session:
 * the module graph is fixed once the page has loaded.
 */
export function missingCoreApi(): string[] {
  const api = core as unknown as Record<string, unknown>
  return REQUIRED.filter((name) => typeof api[name] !== 'function')
}
